import type { BlockId, BlockType } from '@unifyapps/defs/types/block';
import getBlockPipeline from './blockPipeline';

/**
 * Builds a child → parent map by examining block structure (slots, blockIds, blockId, items).
 * This is needed because composite child blocks may not have parentId set.
 */
function buildParentMap(blocks: Record<string, BlockType | undefined>): Record<string, string> {
  const parentMap: Record<string, string> = {};
  const { blocksPipeline } = getBlockPipeline();

  const addChild = (parentId: string, childId?: string | null) => {
    if (childId) parentMap[childId] = parentId;
  };

  for (const [blockId, block] of Object.entries(blocks)) {
    if (!block) continue;

    blocksPipeline.execute({
      block,
      handleSlotsBlock: (b) => {
        Object.values(b.component.slots ?? {}).forEach((slot) => {
          addChild(blockId, slot.blockId);
        });
      },
      handleBlockIdBlock: (b) => {
        addChild(blockId, b.component.content.blockId);
      },
      handleBlockIdsBlock: (b) => {
        b.component.content.blockIds?.forEach((id) => {
          addChild(blockId, id);
        });
      },
      handleContentBlock: (b) => {
        b.component.content.items?.forEach((item) => {
          addChild(blockId, item.blockId);
        });
      },
    });
  }

  return parentMap;
}

/**
 * Returns the set of component types for all ancestors of the given block,
 * including the block itself. Traverses using structural parent references
 * (slots, blockIds) with fallback to block.parentId.
 */
export function getAncestorBlockTypes(
  blocks: Record<string, BlockType | undefined>,
  startBlockId: string,
): Set<string> {
  const parentMap = buildParentMap(blocks);
  const types = new Set<string>();
  const visited = new Set<string>();
  let currentId: string | undefined = startBlockId;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const block = blocks[currentId];
    if (!block) break;
    types.add(block.component.componentType);
    currentId = parentMap[currentId] ?? block.parentId;
  }

  return types;
}

/**
 * Returns the ordered ancestor chain of a block — top-most ancestor first,
 * immediate parent last — as { blockId, label } pairs. Excludes the block
 * itself. Resolves each parent structurally (slots/blockIds/items) with a
 * `block.parentId` fallback, because composite child blocks may not have
 * parentId set — walking parentId alone truncates the chain for exactly the
 * slot-nested blocks callers care about.
 */
export function getBlockAncestors(
  blocks: Record<string, BlockType | undefined>,
  startBlockId: string,
): { blockId: BlockId; label: string }[] {
  const parentMap = buildParentMap(blocks);
  const ancestors: { blockId: BlockId; label: string }[] = [];
  const visited = new Set<string>();
  let currentId: string | undefined = parentMap[startBlockId] ?? blocks[startBlockId]?.parentId;

  while (currentId && !visited.has(currentId)) {
    visited.add(currentId);
    const block = blocks[currentId];
    if (!block) break;
    ancestors.push({ blockId: currentId, label: block.displayName || currentId });
    currentId = parentMap[currentId] ?? block.parentId;
  }

  // reverse so the trail reads top-most ancestor → immediate parent
  ancestors.reverse();
  return ancestors;
}
